import {DOCUMENT} from '@angular/common';
import {Directive, ElementRef, HostListener, Inject, Input, OnDestroy, OnInit, Renderer2} from '@angular/core';

@Directive({
  selector: '[appTooltip]',
  standalone: false
})
export class TooltipDirective implements OnDestroy, OnInit {
  private static nextId = 0;
  private static activePopover?: TooltipDirective;
  @Input() appTooltip = '';
  private readonly viewportMargin = 8;
  private readonly triggerGap = 6;
  private tooltipElement?: HTMLElement;
  private describedByBeforeTooltip: string | null = null;
  private isPopoverOpen = false;
  private pointerActivationIsTouch = false;

  constructor(
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly renderer: Renderer2,
    @Inject(DOCUMENT) private readonly document: Document) {}

  ngOnInit() {
    if (!this.isInfoIcon()) {
      return;
    }

    const host = this.elementRef.nativeElement;
    if (!host.hasAttribute('tabindex')) {
      this.renderer.setAttribute(host, 'tabindex', '0');
    }
    this.renderer.addClass(host, 'app-tooltip-info-trigger');
  }

  @HostListener('pointerdown', ['$event'])
  onPointerDown(event: PointerEvent) {
    this.pointerActivationIsTouch = event.pointerType === 'touch';
  }

  @HostListener('pointerenter', ['$event'])
  onPointerEnter(event: PointerEvent) {
    if (event.pointerType !== 'mouse') {
      return;
    }

    this.showTooltip();
  }

  @HostListener('pointerleave', ['$event'])
  onPointerLeave(event: PointerEvent) {
    if (event.pointerType !== 'mouse') {
      return;
    }

    this.hideTooltip();
  }

  @HostListener('click', ['$event'])
  onClick(event: MouseEvent) {
    if (this.isInfoIcon() && this.isTouchDevice() && event.detail > 0) {
      event.preventDefault();
      event.stopPropagation();
      if (this.isPopoverOpen) {
        this.hideTooltip();
      } else {
        this.showTooltip(true);
      }
      this.pointerActivationIsTouch = false;
      return;
    }

    if (this.isInfoIcon() && event.detail === 0) {
      this.showTooltip(this.isTouchDevice());
      return;
    }

    this.hideTooltip();
  }

  @HostListener('focus')
  onFocus() {
    if (this.isInfoIcon() && !this.pointerActivationIsTouch) {
      this.showTooltip(this.isTouchDevice());
    }
  }

  @HostListener('blur')
  onBlur() {
    if (this.isInfoIcon()) {
      this.hideTooltip();
    }
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    if (this.isInfoIcon() && event.key === 'Escape') {
      this.hideTooltip();
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.isPopoverOpen || !this.tooltipElement) {
      return;
    }

    const target = event.target;
    if (target instanceof Node &&
        !this.elementRef.nativeElement.contains(target) &&
        !this.tooltipElement.contains(target)) {
      this.hideTooltip();
    }
  }

  @HostListener('window:resize')
  @HostListener('window:blur')
  hideOnViewportChange() {
    this.hideTooltip();
  }

  ngOnDestroy() {
    this.hideTooltip();
  }

  private showTooltip(asPopover = false) {
    const label = this.appTooltip?.trim();
    const windowRef = this.document.defaultView;
    if (!label || !windowRef || !this.document.body || this.tooltipElement) {
      return;
    }

    if (asPopover && TooltipDirective.activePopover && TooltipDirective.activePopover !== this) {
      TooltipDirective.activePopover.hideTooltip();
    }

    const host = this.elementRef.nativeElement;
    const tooltip = this.renderer.createElement('div') as HTMLElement;
    const tooltipId = `app-tooltip-${++TooltipDirective.nextId}`;

    this.renderer.addClass(tooltip, 'app-tooltip-overlay');
    if (asPopover) {
      this.renderer.addClass(tooltip, 'app-tooltip-popover');
    }
    this.renderer.setAttribute(tooltip, 'id', tooltipId);
    this.renderer.setAttribute(tooltip, 'role', 'tooltip');
    this.renderer.setStyle(tooltip, 'visibility', 'hidden');
    this.renderer.setProperty(tooltip, 'textContent', label);
    this.renderer.appendChild(this.document.body, tooltip);
    this.tooltipElement = tooltip;
    this.isPopoverOpen = asPopover;
    if (asPopover) {
      TooltipDirective.activePopover = this;
    }

    this.describedByBeforeTooltip = host.getAttribute('aria-describedby');
    this.renderer.setAttribute(
      host,
      'aria-describedby',
      [this.describedByBeforeTooltip, tooltipId].filter(Boolean).join(' ')
    );

    this.positionTooltip(host, tooltip, windowRef);
    this.renderer.removeStyle(tooltip, 'visibility');
  }

  private positionTooltip(host: HTMLElement, tooltip: HTMLElement, windowRef: Window) {
    const hostRect = host.getBoundingClientRect();
    const tooltipRect = tooltip.getBoundingClientRect();
    let placement = 'top';
    let top = hostRect.top - tooltipRect.height - this.triggerGap;

    if (top < this.viewportMargin) {
      placement = 'bottom';
      top = hostRect.bottom + this.triggerGap;
    }

    const maxTop = Math.max(
      this.viewportMargin,
      windowRef.innerHeight - tooltipRect.height - this.viewportMargin
    );
    top = Math.min(
      Math.max(this.viewportMargin, top),
      maxTop
    );

    const centeredLeft = hostRect.left + (hostRect.width - tooltipRect.width) / 2;
    const maxLeft = Math.max(
      this.viewportMargin,
      windowRef.innerWidth - tooltipRect.width - this.viewportMargin
    );
    const left = Math.min(
      Math.max(this.viewportMargin, centeredLeft),
      maxLeft
    );

    this.renderer.setAttribute(tooltip, 'data-placement', placement);
    this.renderer.setStyle(tooltip, 'top', `${Math.round(top)}px`);
    this.renderer.setStyle(tooltip, 'left', `${Math.round(left)}px`);
  }

  private hideTooltip() {
    if (!this.tooltipElement) {
      return;
    }

    this.renderer.removeChild(this.document.body, this.tooltipElement);
    this.tooltipElement = undefined;
    this.isPopoverOpen = false;
    if (TooltipDirective.activePopover === this) {
      TooltipDirective.activePopover = undefined;
    }

    if (this.describedByBeforeTooltip) {
      this.renderer.setAttribute(
        this.elementRef.nativeElement,
        'aria-describedby',
        this.describedByBeforeTooltip
      );
    } else {
      this.renderer.removeAttribute(this.elementRef.nativeElement, 'aria-describedby');
    }
    this.describedByBeforeTooltip = null;
  }

  private isInfoIcon() {
    const host = this.elementRef.nativeElement;
    return host.tagName.toLowerCase() === 'ion-icon' &&
      host.getAttribute('name') === 'information-circle-outline';
  }

  private isTouchDevice() {
    return this.document.defaultView?.matchMedia('(hover: none), (pointer: coarse)').matches ?? false;
  }
}
